import { Badge, Button, cardClass, fieldClass, selectClass, Table } from '@ledgerloom/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, propose } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { useSession } from '../session';
import { Breadcrumb, Empty, ErrorBanner, Icon, ListSkeleton, Loading, outcomeMessage, outcomeTone, Time, ToggleGroup, useToast, badgeTone } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { day, money } from '../format';

// Deals: a table of every deal and one deal's record with its stage history,
// activities, and quotes. Patterns: data-display/table, data-display/timeline for
// stage history, content-management/modal for closing, and navigation/load-more
// for the deal list.

interface DealRow { id: string; title: string; status: string; stage_name: string; value_minor: string; currency: string; probability: number; expected_close: string | null; owner_name: string | null; party_name: string | null; organization_name: string | null; created_at: string }

const DEAL_PAGE = 100;

export function Deals() {
  const [status, setStatus] = useState<'open' | 'won' | 'lost'>('open');
  const deals = useApi<DealRow[]>(`/v1/deals?status=${status}&limit=${DEAL_PAGE}`, [status]);
  // Pages after the first, appended by Load more; done once a page comes back short.
  const [extra, setExtra] = useState<{ status: string; rows: DealRow[]; done: boolean } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const mine = extra?.status === status ? extra : null;
  const rows = deals.data ? [...deals.data, ...(mine?.rows ?? [])] : null;
  const hasMore = !!deals.data && (mine ? !mine.done : deals.data.length === DEAL_PAGE);
  usePageTitle('Deals');
  async function more() {
    if (!rows) return;
    setLoadingMore(true); setMoreError(null);
    try {
      const page = await api<DealRow[]>(`/v1/deals?status=${status}&limit=${DEAL_PAGE}&offset=${rows.length}`);
      // The API pages by offset, so a deal created meanwhile can shift one into this page twice.
      const seen = new Set(rows.map((d) => d.id));
      setExtra({ status, rows: [...(mine?.rows ?? []), ...page.filter((d) => !seen.has(d.id))], done: page.length < DEAL_PAGE });
    } catch (e) { setMoreError(e); } finally { setLoadingMore(false); }
  }
  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/pipeline', label: 'Pipeline' }, { label: 'Deals' }]} />
      <div className="page-head"><div><h1>Deals</h1><p>Every deal, by status.</p></div><ToggleGroup label="Show deals that are" value={status} options={[['open', 'Open'], ['won', 'Won'], ['lost', 'Lost']]} onChange={setStatus} /></div>
      <ErrorBanner error={deals.error} />
      <section className={cardClass}>
        {deals.loading && !rows ? <ListSkeleton rows={5} label="Loading deals" /> : !rows?.length ? <Empty title={`No ${status} deals`} /> : (
          <Table label="Deals" id="deal-list">
            <caption className="sr-only">{status} deals</caption>
            <thead><tr><th scope="col">Deal</th><th scope="col">Contact</th><th scope="col">Stage</th><th scope="col">Value</th><th scope="col" className="hide-sm">Close</th><th scope="col" className="hide-sm">Owner</th></tr></thead>
            <tbody>{rows.map((d) => <tr key={d.id}><td><Link to={`/deals/${d.id}`}>{d.title}</Link></td><td className="small">{d.organization_name ?? d.party_name ?? '–'}</td><td className="small">{d.stage_name}</td><td>{money(d.value_minor, d.currency, { cents: false })}</td><td className="small hide-sm">{day(d.expected_close)}</td><td className="small hide-sm">{d.owner_name ?? '–'}</td></tr>)}</tbody>
          </Table>
        )}
      </section>
      <ErrorBanner error={moreError} />
      {hasMore && <div><Button type="submit" variant="secondary" onClick={more} disabled={loadingMore} aria-controls="deal-list">{loadingMore ? 'Loading…' : 'Load more deals'}</Button></div>}
    </div>
  );
}

interface Deal {
  id: string; title: string; status: string; stage_name: string; stage_kind: string; pipeline_name: string; pipeline_id: string; value_minor: string; currency: string; probability: number;
  expected_close: string | null; owner_name: string | null; party_id: string | null; party_name: string | null; organization_id: string | null; organization_name: string | null; lost_reason: string | null; close_note: string | null; version: number; closed_at: string | null;
  stage_history: Array<{ id: number; from_stage: string | null; to_stage: string; to_status: string; entered_at: string; proposal_id: string }>;
  activities: Array<{ id: string; kind: string; subject: string; status: string; due_at: string | null; done_at: string | null; created_at: string }>;
  history: Array<{ seq: string; op: string; proposal_id: string; at: string }>;
}

export function DealDetail() {
  const { id = '' } = useParams();
  const deal = useApi<Deal>(`/v1/deals/${id}`, [id]);
  const quotes = useApi<{ items: Array<{ id: string; number: string; version: number; status: string; total_minor: string; currency: string }> }>(`/v1/quotes?deal=${id}&limit=500`, [id]);
  const pipelines = useApi<{ lost_reasons: Array<{ id: string; label: string }> }>('/v1/pipelines');
  const [closing, setClosing] = useState<'won' | 'lost' | null>(null);
  const [lostReason, setLostReason] = useState('');
  const [note, setNote] = useState('');
  const [activity, setActivity] = useState({ kind: 'call', subject: '', due: '' });
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [marking, setMarking] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const { refreshStats } = useSession();
  const navigate = useNavigate();
  const d = deal.data;
  usePageTitle(d?.title ?? 'Deal');

  async function close() {
    setBusy(true); setError(null);
    try {
      const out = await propose('deal.close', { deal_id: id, base_version: d!.version, outcome: closing, ...(note ? { note } : {}), ...(closing === 'lost' ? { lost_reason_id: lostReason || pipelines.data?.lost_reasons[0]?.id } : {}) });
      toast(outcomeMessage(out, closing === 'won' ? 'Closed won' : 'Closed lost'), outcomeTone(out));
      setClosing(null); refreshStats(); void deal.reload();
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  async function addActivity(e: React.FormEvent) {
    e.preventDefault(); setError(null); setAdding(true);
    try {
      const out = await propose('activity.create', { kind: activity.kind, subject: activity.subject, deal_id: id, ...(d?.party_id ? { party_id: d.party_id } : {}), ...(activity.due ? { due_at: new Date(activity.due).toISOString() } : {}) });
      toast(outcomeMessage(out, 'Activity added'), outcomeTone(out));
      setActivity({ kind: 'call', subject: '', due: '' }); void deal.reload();
    } catch (err) { setError(err); } finally { setAdding(false); }
  }
  async function markDone(activityId: string) {
    setMarking(activityId);
    try {
      const out = await propose('activity.update', { activity_id: activityId, status: 'done' });
      toast(outcomeMessage(out, 'Marked done'), outcomeTone(out)); void deal.reload();
    } catch (err) { toast((err as Error).message, 'error'); } finally { setMarking(null); }
  }

  if (deal.loading && !d) return <Loading label="Loading the deal" />;
  if (!d) return <ErrorBanner error={deal.error} />;
  return (
    <div className="stack">
      <Breadcrumb items={[{ to: '/pipeline', label: 'Pipeline' }, { to: '/deals', label: 'Deals' }, { label: d.title }]} />
      <div className="page-head">
        <div>
          <div className="row"><h1>{d.title}</h1><Badge tone={badgeTone(d.status === 'won' ? 'ok' : d.status === 'lost' ? 'danger' : 'info')}>{d.status === 'open' ? d.stage_name : d.status}</Badge></div>
          <p>{money(d.value_minor, d.currency)} · {d.probability}% · {d.organization_name ?? d.party_name ?? 'No contact'} · {d.owner_name ?? 'No owner'}</p>
        </div>
        {d.status === 'open' && (
          <div className="row">
            <Button type="submit" variant="secondary" onClick={() => navigate(`/quotes/new?deal=${id}`)}><Icon name="quote" />New quote</Button>
            <Button type="submit" onClick={() => setClosing('won')}>Close won</Button>
            <Button type="submit" variant="danger" onClick={() => setClosing('lost')}>Close lost…</Button>
          </div>
        )}
      </div>
      <ErrorBanner error={closing ? null : error} />
      <div className="grid-main">
        <div className="stack">
          <section className={cardClass} aria-labelledby="acts-h">
            <div className="card-head"><h2 id="acts-h">Activities</h2></div>
            {!d.activities.length ? <Empty title="No activities yet" /> : (
              <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {d.activities.map((a) => (
                  <li key={a.id} className="list-item">
                    <div className="grow"><div className="item-title">{a.subject}</div><div className="item-sub">{a.kind} · {a.status}{a.due_at ? ` · due ${day(a.due_at)}` : ''}</div></div>
                    {a.status === 'open' && <Button type="submit" variant="secondary" size="sm" disabled={marking === a.id} aria-busy={marking === a.id || undefined} onClick={() => void markDone(a.id)}>Mark done<span className="sr-only">: {a.subject}</span></Button>}
                  </li>
                ))}
              </ul>
            )}
            <form className="card-pad form-grid" style={{ borderTop: '1px solid var(--border)' }} onSubmit={addActivity}>
              <Field label="Kind"><select className={selectClass} value={activity.kind} onChange={(e) => setActivity({ ...activity, kind: e.target.value })}>{['call', 'meeting', 'task', 'email', 'deadline', 'note'].map((k) => <option key={k} value={k}>{k}</option>)}</select></Field>
              <Field label="Due"><input className={fieldClass} type="datetime-local" value={activity.due} onChange={(e) => setActivity({ ...activity, due: e.target.value })} /></Field>
              <Field label="What" className="full" required><input className={fieldClass} value={activity.subject} onChange={(e) => setActivity({ ...activity, subject: e.target.value })} /></Field>
              <div className="full"><Button type="submit" variant="secondary" disabled={adding}>{adding ? 'Adding…' : 'Add activity'}</Button></div>
            </form>
          </section>
          <section className={cardClass} aria-labelledby="dq-h">
            <div className="card-head"><h2 id="dq-h">Quotes</h2></div>
            {!quotes.data?.items.length ? <Empty title="No quotes yet" /> : (
              <ul className="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {quotes.data.items.map((q) => <li key={q.id}><Link className="list-item" to={`/quotes/${q.id}`}><span className="grow item-title">{q.number} v{q.version}</span><Badge>{q.status}</Badge><span>{money(q.total_minor, q.currency)}</span></Link></li>)}
              </ul>
            )}
          </section>
        </div>
        <section className={`${cardClass} card-pad stack`} style={{ gap: 10 }} aria-labelledby="stages-h">
          <h2 id="stages-h">Stage history</h2>
          <ol className="timeline">
            {d.stage_history.map((h) => <li key={h.id} className={h.to_status === 'won' ? 'ok' : h.to_status === 'lost' ? 'danger' : ''}>{h.from_stage ? `${h.from_stage} → ` : ''}{h.to_stage} · <Time iso={h.entered_at} /> · <Link to={`/activity/${h.proposal_id}`}>decision</Link></li>)}
          </ol>
          {d.lost_reason && <div className="small">Lost: {d.lost_reason}{d.close_note ? ` · ${d.close_note}` : ''}</div>}
          <div className="faint">{d.pipeline_name} pipeline · expected close {day(d.expected_close)}</div>
        </section>
      </div>
      <ConfirmDialog open={!!closing} title={closing === 'won' ? `Close ${d.title} as won?` : `Close ${d.title} as lost?`} confirmLabel={closing === 'won' ? 'Close won' : 'Close lost'} tone={closing === 'lost' ? 'danger' : 'primary'} busy={busy} onConfirm={close} onCancel={() => setClosing(null)}>
        <ErrorBanner error={error} />
        {closing === 'lost' && <Field label="Why was it lost?" required><select className={selectClass} value={lostReason} onChange={(e) => setLostReason(e.target.value)}>{pipelines.data?.lost_reasons.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</select></Field>}
        <Field label="Note" hint="Optional."><input className={fieldClass} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </ConfirmDialog>
    </div>
  );
}
